import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  startTransition,
} from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Divider, Tooltip, Whisper } from 'rsuite'
import FileValidationModal from './dataset_form/FileValidationModal'
import { FormSelectPicker } from '../../../../ui_reusable/StandardSelectPickers'
import {
  aggregationMethodOptions,
  baselineForNonMediaOptions,
  categoryOptions,
  influenceOptions,
  inputCategoryOptions,
  investmentOptions,
  roiAssumptionOptions,
  unitOptions,
} from '../../../../../utils/dropdowns'
import {
  Container,
  FormGroup,
  Label,
  LabelWithoutMarginNoWrap,
  ButtonContainer,
  ErrorMessage,
  SuccessContainer,
  ServerErrorMessage,
} from '../../../../ui_reusable/styles/CreateInputUtils.styles'
import {
  VStack,
  HStack,
  SelectorHStack,
  DescriptionContainer,
  VStackBroad,
  HStackRadioComplex,
  TopContainer,
  LeftChildOfTopContainer,
  RightChildOfTopContainer,
  RequiredLabelStar,
  InformationText,
} from './styles/DatasetForm.styles'
import { StandardButton } from '../../../../ui_reusable/styles/Button.styles'
import {
  StandardCheckbox,
  StandardInput,
} from '../../../../ui_reusable/StandardInputs'
import CheckmarkAnimation from '../../../../ui_reusable/CheckmarkAnimation'
import UniversalLoader from '../../../../ui_reusable/UniversalLoader'
import StandardComboBox from '../../../../ui_reusable/StandardComboBox'
import {
  StandardRadio,
  RadioProvider,
} from '../../../../ui_reusable/StandardRadioButtons'
import RegionCreateModal from './RegionCreateModal'
import { useAuth } from '../../../../../context/AuthContext'
import FileUploadSection, {
  FileUploadSectionRef,
} from './dataset_form/FileUploadSection'
import DriverScopeSection, {
  DriverScopeSectionRef,
} from './dataset_form/DriverScopeSection'
import type { SearchItem, FormProps } from '../types/DatasetHandlingTypes'
import {
  DatasetCreateRequest,
  DatasetCreateRequestAggregationMethod,
  DatasetCreateRequestCategory,
  DatasetCreateRequestInfluence,
  DatasetCreateRequestInvestment,
  DatasetCreateRequestUnits,
  MediaParametersRequestInputCategory,
  MediaParametersRequestRoiAssumption,
} from '@/api/generated/models'
import { validateDatasetForm, handleServerErrors } from './dataset_form/utils'
import { useChannelsList } from '../../../../../api/generated/endpoints/media-channels/media-channels'
import { useRegionsList } from '../../../../../api/generated/endpoints/regions/regions'
import { useChannelsCreate } from '../../../../../api/generated/endpoints/media-channels/media-channels'
import { useRegionsCreate } from '../../../../../api/generated/endpoints/regions/regions'
import { useDatasetsPairableList } from '../../../../../api/generated/endpoints/datasets/datasets'
import { useQuery, useQueryClient } from '@tanstack/react-query'

/**
 * DATASET FORM COMPONENT
 *
 * A multi-purpose form that handles creation and editing of:
 * 1. Dataset (Contextual/External)
 * 2. Dataset (Client/Internal)
 * 3. Media Channel Dataset
 * 4. Regional Dataset (Segment)
 * 5. Media Channel (inline creation)
 * 6. Region (inline creation)
 *
 * FORM STATE VARIABLES (control behavior):
 * - isSegment: boolean - If true, creates RegionalDataset instead of Dataset
 * - driverType: 'contextual' | 'client' - External vs client-owned dataset
 * - part_of_media_channel: boolean - If true, shows media channel fields
 * - isEdit: boolean - Edit mode vs create mode
 *
 * DATA FLOW:
 * User Input → Form Validation → Parent onSubmit → API Transformation → Backend
 */

const Form: React.FC<FormProps> = ({
  initialData,
  onSubmit,
  submitButtonText = 'SAVE DATASET',
  successMessage = 'Dataset saved successfully!',
  isEdit = false,
  onSuccess,
  resetOnSuccess = false,
}) => {
  const fileUploadRef = useRef<FileUploadSectionRef>(null)
  const driverScopeSectionRef = useRef<DriverScopeSectionRef>(null)

  // Region creation modal state
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false)
  const [pendingRegionName, setPendingRegionName] = useState('')

  const methods = useForm<DatasetCreateRequest>({
    defaultValues: {
      client_id: initialData?.client?.id?.toString(),
      market_ids: initialData?.markets?.map((m) => Number(m.id)) || [],
      media_channel_id: initialData?.media_channel?.id,
      interval: initialData?.interval,
      quality: initialData?.quality,
      influence: initialData?.influence,
      investment: initialData?.investment,
      aggregation_method: initialData?.aggregation_method ?? 'mean',
      total_observations: initialData?.total_observations,
      included_observations: initialData?.included_observations,
      media_parameters: {
        channel_lag: initialData?.media_parameters?.channel_lag,
        spend_constraint: null,
        roi_assumption: initialData?.media_parameters?.roi_assumption,
        input_category: initialData?.media_parameters?.input_category,
        paired_frequency_dataset_id: initialData?.paired_frequency_dataset?.id,
        paired_reach_dataset_id: initialData?.paired_reach_dataset?.id,
      },
      part_of_media_channel: initialData?.part_of_media_channel,
      slug: initialData?.slug,
      start_at: initialData?.start_at,
      end_at: initialData?.end_at,
      status_text: initialData?.status_text,
      name: initialData?.name,
      source: initialData?.source,
      description: initialData?.description,
      category: initialData?.category,
      units: initialData?.units,
      is_contextual: initialData?.is_contextual ?? true,
      non_media_baseline: initialData?.non_media_baseline,
      apply_smoothing: initialData?.apply_smoothing,
      smoothing_factor: initialData?.smoothing_factor,
      aggregate_regional_variants: initialData?.aggregate_regional_variants,
    },
  })

  const {
    register,
    getValues,
    handleSubmit,
    setValue,
    watch,
    reset: formReset,
    formState: { errors },
    setError,
    clearErrors,
  } = methods

  // Get all form field names for error mapping
  const formFieldNames = useMemo(() => {
    return Object.keys(getValues()) as (keyof DatasetCreateRequest)[]
  }, [getValues])

  // UI state
  const [showSuccess, setShowSuccess] = useState<boolean>(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false)

  // Form mode state
  const [driverType, setDriverType] = useState<'contextual' | 'client'>(
    (initialData?.is_contextual ?? true) ? 'contextual' : 'client',
  )
  const [isSegment, setIsSegment] = useState<boolean>(false)

  // Selected items for ComboBoxes
  const [selectedChannelsItems, setSelectedChannelsItems] = useState<
    SearchItem[]
  >([])
  const [selectedRegionsItems, setSelectedRegionsItems] = useState<
    SearchItem[]
  >([])
  const [selectedParentDatasetItems, setSelectedParentDatasetItems] = useState<
    SearchItem[]
  >([])
  const [
    selectedPairedFrequencyDatasetItems,
    setSelectedPairedFrequencyDatasetItems,
  ] = useState<SearchItem[]>([])
  const [selectedPairedReachDatasetItems, setSelectedPairedReachDatasetItems] =
    useState<SearchItem[]>([])

  const { user, isAdmin, isClientManager } = useAuth()

  // Auto-set client driver type for client managers
  useEffect(() => {
    if (isClientManager) {
      setDriverType('client')
    }
  }, [isClientManager])

  const widthSelectors = '12.85rem'

  const queryClient = useQueryClient()

  /**
   * Mutation for creating media channels inline
   */
  const createChannelMutation = useChannelsCreate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/api/channels/'] })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/with-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/without-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/'],
        })
      },
    },
  })

  /**
   * Mutation for creating regions inline
   */
  const createRegionMutation = useRegionsCreate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/api/regions/'] })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/with-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/without-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/'],
        })
      },
    },
  })

  // Watch form fields needed for pairable API
  const inputCategory = watch('media_parameters.input_category')
  const interval = watch('interval')
  const startAt = watch('start_at')
  const endAt = watch('end_at')
  const clientId = watch('client_id')
  const isPartOfMediaChannel = watch('part_of_media_channel')

  const { data: channelsData } = useChannelsList(
    {
      client_id: Number(clientId),
    },
    {
      query: {
        enabled: !isSegment && !!isPartOfMediaChannel && !!clientId,
      },
    },
  )
  const channels = channelsData?.data

  const searchableChannels: SearchItem[] = useMemo(() => {
    return (
      channels?.map((channel) => ({
        id: String(channel.id),
        name: channel.name,
      })) ?? []
    )
  }, [channels])

  const parentDriverSelected = selectedParentDatasetItems.length > 0
  const parentClientId = selectedParentDatasetItems[0]?.client?.id
  const regionScope: 'contextual' | 'client' = parentClientId
    ? 'client'
    : 'contextual'

  const { data: regionsList } = useRegionsList(
    {
      scope: regionScope,
      ...(parentClientId ? { client_id: Number(parentClientId) } : {}),
    },
    {
      query: {
        enabled: isSegment && parentDriverSelected,
      },
    },
  )
  const regions = regionsList?.data

  const searchableRegions: SearchItem[] = useMemo(() => {
    return (
      regions?.map((region) => ({
        id: String(region.id),
        name: region.name,
      })) ?? []
    )
  }, [regions])

  // Parent-driver picker: lite list, active-client drivers + contextual (no client)
  const { data: searchableDatasets = [] } = useQuery({
    queryKey: ['/api/v2/datasets/', { ordering: 'name', active_client: true }],
    queryFn: async (): Promise<SearchItem[]> => {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v2/datasets/?ordering=name&active_client=true`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )
      if (!response.ok) {
        throw new Error('Failed to load datasets')
      }
      const json = await response.json()
      const rows = json.data || json || []
      return rows.map(
        (dataset: { id?: number; name?: string; client_id?: number | null }) => ({
          id: String(dataset.id ?? 0),
          name: dataset.name || '',
          ...(dataset.client_id
            ? { client: { id: String(dataset.client_id), name: '' } }
            : {}),
        }),
      )
    },
    enabled: isSegment,
    staleTime: 10 * 60 * 1000,
  })

  // Determine the opposite category for pairing (reach pairs with frequency, frequency pairs with reach)
  const pairableCategory =
    inputCategory === 'reach'
      ? 'frequency'
      : inputCategory === 'frequency'
        ? 'reach'
        : undefined

  // Build pairable API params - only call if we have all required data
  const shouldFetchPairable =
    isPartOfMediaChannel && pairableCategory && interval && startAt && endAt

  const pairableParams = shouldFetchPairable
    ? {
        media_input_category: pairableCategory as 'reach' | 'frequency',
        interval: interval as 'daily' | 'weekly' | 'monthly',
        start_date: startAt,
        end_date: endAt,
        ...(driverType === 'client' && clientId
          ? { client_id: Number(clientId) }
          : {}),
      }
    : undefined

  const { data: pairableListData } = useDatasetsPairableList(
    pairableParams || {
      media_input_category: 'frequency',
      interval: 'monthly',
      start_date: '',
      end_date: '',
    },
    {
      query: {
        enabled: !!shouldFetchPairable,
      },
    },
  )

  // Transform pairable datasets to SearchItem format with metadata
  const searchablePairableDatasets: (SearchItem & {
    isActive?: boolean
    comment?: string
  })[] = useMemo(() => {
    const pairableDatasets = pairableListData?.data || []

    return pairableDatasets.map((dataset: any) => ({
      id: String(dataset.id),
      name: dataset.name,
      isActive: dataset.is_active === 'True' || dataset.is_active === true,
      comment: dataset.comment || '',
    }))
  }, [pairableListData])

  /**
   * Initialize selected items for edit mode
   */
  useEffect(() => {
    if (isEdit && initialData) {
      // Initialize selected channel if exists
      if (initialData.media_channel?.id && initialData.media_channel?.name) {
        setSelectedChannelsItems([
          {
            id: String(initialData.media_channel.id),
            name: initialData.media_channel.name,
            isNew: false,
          },
        ])
      }

      // Initialize selected paired frequency dataset if exists
      if (
        initialData.paired_frequency_dataset?.id &&
        initialData.paired_frequency_dataset?.name
      ) {
        setSelectedPairedFrequencyDatasetItems([
          {
            id: String(initialData.paired_frequency_dataset.id),
            name: initialData.paired_frequency_dataset.name,
            isNew: false,
          },
        ])
      }

      // Initialize selected paired reach dataset if exists
      if (
        initialData.paired_reach_dataset?.id &&
        initialData.paired_reach_dataset?.name
      ) {
        setSelectedPairedReachDatasetItems([
          {
            id: String(initialData.paired_reach_dataset.id),
            name: initialData.paired_reach_dataset.name,
            isNew: false,
          },
        ])
      }
    }
  }, [isEdit, initialData])

  /**
   * Reset roi_assumption when input_category changes away from 'spend'
   */
  useEffect(() => {
    // Only reset if input_category is not 'spend' and roi_assumption has a value
    if (inputCategory !== 'spend') {
      const currentRoiAssumption = getValues('media_parameters.roi_assumption')
      if (currentRoiAssumption) {
        setValue('media_parameters.roi_assumption', undefined)
        clearErrors('media_parameters.roi_assumption')
      }
    }
  }, [inputCategory, setValue, clearErrors, getValues])

  // HANDLERS - CHANNEL MANAGEMENT

  /**
   * Handles channel selection change
   */
  const handleChannelChange = (items: SearchItem[]) => {
    startTransition(() => {
      setSelectedChannelsItems(items)
      if (items && items.length > 0) {
        const selectedItem = items[0]
        // Only set numeric ID if it's an existing channel from backend
        if (!selectedItem.isNew) {
          setValue('media_channel_id', Number(selectedItem.id))
          clearErrors('media_channel_id')
        } else {
          setValue('media_channel_id', undefined)
        }
      } else {
        setValue('media_channel_id', undefined)
      }
    })
  }

  /**
   * Creates a new channel inline and selects it
   */
  const handleChannelCreate = async (newChannelName: string) => {
    const clientId = getValues('client_id')

    if (!clientId) {
      setServerError('Please select a client before creating a channel')
      return
    }

    try {
      const data = await createChannelMutation.mutateAsync({
        data: {
          name: newChannelName,
          client_id: Number(clientId),
        },
      })

      const newChannelId = data.data.id
      if (!newChannelId) {
        setServerError('Failed to create channel: No ID returned')
        return
      }

      const newSearchItem: SearchItem = {
        id: String(newChannelId),
        name: newChannelName,
        isNew: false,
      }

      setSelectedChannelsItems([newSearchItem])
      setValue('media_channel_id', newChannelId)
      clearErrors('media_channel_id')
    } catch (e) {
      setServerError('Failed to create channel')
      console.error('Error creating channel:', e)
    }
  }

  // HANDLERS - REGION MANAGEMENT

  /**
   * Handles region selection change
   */
  const handleRegionChange = (items: SearchItem[]) => {
    startTransition(() => {
      setSelectedRegionsItems(items)
    })
  }

  /**
   * Handles parent dataset selection change
   */
  const handleParentDatasetChange = (items: SearchItem[]) => {
    const nextClientId = items[0]?.client?.id
    setValue('client_id', nextClientId ? String(nextClientId) : '')
    startTransition(() => {
      setSelectedParentDatasetItems(items)
      setSelectedRegionsItems([])
    })
  }

  /**
   * Handles paired frequency dataset selection change
   */
  const handlePairedFrequencyDatasetChange = (items: SearchItem[]) => {
    startTransition(() => {
      setSelectedPairedFrequencyDatasetItems(items)
      if (items && items.length > 0) {
        setValue(
          'media_parameters.paired_frequency_dataset_id',
          Number(items[0].id),
        )
        clearErrors('media_parameters.paired_frequency_dataset_id')
      } else {
        setValue('media_parameters.paired_frequency_dataset_id', undefined)
      }
    })
  }

  /**
   * Handles paired reach dataset selection change
   */
  const handlePairedReachDatasetChange = (items: SearchItem[]) => {
    startTransition(() => {
      setSelectedPairedReachDatasetItems(items)
      if (items && items.length > 0) {
        setValue(
          'media_parameters.paired_reach_dataset_id',
          Number(items[0].id),
        )
        clearErrors('media_parameters.paired_reach_dataset_id')
      } else {
        setValue('media_parameters.paired_reach_dataset_id', undefined)
      }
    })
  }

  /**
   * Opens the region creation modal
   */
  const handleRegionCreate = async (newRegionName: string) => {
    const clientId = getValues('client_id')

    if (!clientId && driverType === 'client') {
      setServerError('Please select a client before creating a region')
      return
    }

    // Open modal with the name the user typed
    setPendingRegionName(newRegionName)
    setIsRegionModalOpen(true)
  }

  /**
   * Creates a new region with name and population from modal
   */
  const handleRegionModalConfirm = async (name: string, population: number) => {
    const clientId = getValues('client_id')

    try {
      const data = await createRegionMutation.mutateAsync({
        data: {
          name: name,
          client_id: clientId ? Number(clientId) : undefined,
          population: population,
        },
      })

      const newRegionId = data.data.id
      if (!newRegionId) {
        setServerError('Failed to create segment: No ID returned')
        return
      }

      const newSearchItem: SearchItem = {
        id: String(newRegionId),
        name: name,
        isNew: false,
      }

      setSelectedRegionsItems([newSearchItem])
    } catch (e) {
      setServerError('Failed to create segment')
      console.error('Error creating region:', e)
    }
  }

  // HANDLERS - DRIVER TYPE

  /**
   * Handles driver type change (contextual vs client)
   */
  const handleDriverTypeChange = (type: 'contextual' | 'client') => {
    setDriverType(type)

    if (type === 'contextual') {
      // Contextual datasets cannot be part of media channel
      setValue('is_contextual', true)
      setValue('client_id', '')
      setValue('part_of_media_channel', false)
      clearErrors('client_id')
    } else {
      setValue('is_contextual', false)
    }
  }

  // FORM RESET

  /**
   * Resets form to initial state
   */
  const resetForm = () => {
    formReset({
      file: { name: '', data: '', type: '' },
      name: '',
      source: '',
      description: '',
      interval: 'monthly',
      aggregation_method: 'mean',
      units: 'value',
      influence: '',
      investment: '',
      is_contextual: true,
      client_id: '',
      market_ids: [],
      media_channel_id: undefined,
      apply_smoothing: false,
      category: '',
      media_parameters: {
        input_category: undefined,
        channel_lag: undefined,
        spend_constraint: undefined,
        roi_assumption: undefined,
      },
    } as DatasetCreateRequest)

    setSelectedChannelsItems([])
    setSelectedRegionsItems([])
    setSelectedParentDatasetItems([])
    setDriverType('contextual')
    setIsSegment(false)

    if (fileUploadRef.current) {
      fileUploadRef.current.reset()
    }

    if (driverScopeSectionRef.current) {
      driverScopeSectionRef.current.reset()
    }
  }

  // FORM SUBMISSION

  /**
   * Validates and submits the form
   *
   * VALIDATION FLOW:
   * 1. Run form-level validation based on current mode (segment vs dataset)
   * 2. If validation passes, transform data and call parent onSubmit
   * 3. Parent routes to appropriate API endpoint
   * 4. Handle success/error responses
   */
  const onFormSubmit = async (formData: DatasetCreateRequest) => {
    setServerError(null)

    // Get file from upload section
    const fileToSubmit = fileUploadRef.current?.getFileForSubmission()

    // Validate based on current form mode
    const validationErrors = validateDatasetForm(
      formData,
      fileToSubmit,
      selectedParentDatasetItems,
      selectedRegionsItems,
      {
        isSegment,
        isContextual: driverType === 'contextual',
        partOfMediaChannel: watch('part_of_media_channel') || false,
        isEdit,
      },
    )

    // Set validation errors on form
    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => {
        setError(error.field as any, {
          type: 'validation',
          message: error.message,
        })
      })
      return
    }

    // Prepare payload
    let payload: any = { ...formData }

    // For segments, add extra data needed by parent
    if (isSegment) {
      payload.isSegment = true
      payload.selectedParentDataset = selectedParentDatasetItems
      payload.selectedRegions = selectedRegionsItems
    }

    setIsSubmitting(true)

    try {
      const result = await onSubmit(payload, fileToSubmit!)

      if (result && result.success) {
        // Show success animation
        setShowSuccess(true)

        // Reset form if configured
        if (resetOnSuccess) {
          resetForm()
        }

        // Hide success after animation
        setTimeout(() => {
          setShowSuccess(false)
        }, 1500)

        // Trigger accordion collapse after animation
        setTimeout(() => {
          if (onSuccess) {
            onSuccess()
          }
        }, 1750)

        // Clear server errors
        setTimeout(() => {
          setServerError(null)
        }, 2000)
      } else if (result && result.error) {
        // Handle server errors
        const { fieldErrors, unmappedErrors } = handleServerErrors(
          result.error,
          formFieldNames,
        )

        // Set field-level errors
        Object.entries(fieldErrors).forEach(([field, error]) => {
          setError(field as any, error)
        })

        // Set general server error for unmapped errors
        if (unmappedErrors.length > 0) {
          setServerError(unmappedErrors.join('; '))
        }
      }
    } catch (error) {
      console.error('Unexpected error:', error)
      setServerError('An unexpected error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  // INITIAL DATA FOR CHILD COMPONENTS

  const initialClientForScope = useMemo(
    () =>
      isEdit && !initialData?.is_contextual && initialData?.client
        ? [{ id: String(initialData.client.id), name: initialData.client.name }]
        : [],
    [isEdit, initialData],
  )

  const initialMarketsForScope = useMemo(
    () =>
      isEdit && initialData?.markets
        ? initialData.markets.map(
            (market) =>
              ({
                id: market.id,
                name: market.name,
                icon: market.icon,
              }) as SearchItem,
          )
        : [],
    [isEdit, initialData],
  )

  // CLEANUP

  useEffect(() => {
    return () => {
      // Prevent state updates after unmount
      setShowSuccess(false)
      setServerError(null)
      setIsSubmitting(false)
    }
  }, [])

  return (
    <Container>
      <UniversalLoader
        show={isSubmitting}
        content={isEdit ? 'Updating dataset...' : 'Creating dataset...'}
      />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <FormGroup>
            <VStackBroad>
              {/* TOP SECTION: File Upload + Driver Scope */}
              <HStack>
                <TopContainer>
                  <LeftChildOfTopContainer>
                    <FileUploadSection
                      ref={fileUploadRef}
                      isEdit={isEdit}
                      initialFile={
                        initialData?.file
                          ? { name: initialData.file.split('/').pop() || '' }
                          : undefined
                      }
                      isRequired={isSegment}
                    />
                  </LeftChildOfTopContainer>
                  <RightChildOfTopContainer>
                    {/* Only show driver scope for non-segments and admin/owner users */}
                    {!isSegment && (isAdmin || user?.role === 'owner') && (
                      <DriverScopeSection
                        ref={driverScopeSectionRef}
                        driverType={driverType}
                        isAdmin={isAdmin}
                        initialMarkets={initialMarketsForScope}
                        initialClient={initialClientForScope}
                      />
                    )}
                  </RightChildOfTopContainer>
                </TopContainer>
              </HStack>

              {/* DRIVER TYPE & MODE SELECTION */}
              {(isAdmin || user?.role === 'owner') && (
                <HStack style={{ marginTop: '2rem' }}>
                  {/* Segment checkbox (admin/owner only) */}
                  <HStackRadioComplex>
                    <StandardCheckbox
                      checked={isSegment}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setIsSegment(!isSegment)
                      }}
                      data-testid="is-segment-checkbox"
                    />
                    <LabelWithoutMarginNoWrap>
                      Is segment?
                    </LabelWithoutMarginNoWrap>
                  </HStackRadioComplex>

                  {/* Driver type selection (only for non-segments) */}
                  {!isSegment && (
                    <>
                      <RadioProvider
                        group="driverType"
                        initValue={driverType}
                        onChange={(value) =>
                          handleDriverTypeChange(
                            value as 'contextual' | 'client',
                          )
                        }
                      >
                        <HStackRadioComplex>
                          <StandardRadio
                            group="driverType"
                            value="contextual"
                          />
                          <LabelWithoutMarginNoWrap>
                            External driver
                          </LabelWithoutMarginNoWrap>
                        </HStackRadioComplex>
                        <HStackRadioComplex>
                          <StandardRadio group="driverType" value="client" />
                          <LabelWithoutMarginNoWrap>
                            Internal driver
                          </LabelWithoutMarginNoWrap>
                        </HStackRadioComplex>
                      </RadioProvider>

                      {/* Media channel checkbox (only for client drivers) */}
                      <HStackRadioComplex>
                        <StandardCheckbox
                          checked={watch('part_of_media_channel')}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            setValue(
                              'part_of_media_channel',
                              e.target.checked,
                              { shouldValidate: true },
                            )
                          }}
                          data-testid="part-of-media-channel-checkbox"
                          disabled={driverType !== 'client'}
                        />
                        <LabelWithoutMarginNoWrap
                          style={{
                            opacity: driverType !== 'client' ? '50%' : '100%',
                          }}
                        >
                          Media channel
                        </LabelWithoutMarginNoWrap>
                      </HStackRadioComplex>
                    </>
                  )}
                </HStack>
              )}

              {/* ========================================================== */}
              {/* BASIC INFORMATION SECTION */}
              {/* ========================================================== */}
              <DescriptionContainer>
                {/* Parent Driver (for segments) OR Driver Name (for datasets) */}
                <VStack style={{ flexGrow: 0.5 }}>
                  <Label>
                    {isSegment ? 'Parent driver' : 'Driver name'}
                    <RequiredLabelStar>*</RequiredLabelStar>
                  </Label>
                  {isSegment ? (
                    <StandardComboBox
                      data={searchableDatasets}
                      onChange={handleParentDatasetChange}
                      placeholder="Select parent driver"
                      maxSelected={1}
                      alwaysShowList={true}
                      valueList={selectedParentDatasetItems}
                      style={{
                        width: '100%',
                        padding: '0px',
                        paddingTop: '0px',
                        paddingBottom: '0px',
                        alignItems: 'center',
                      }}
                      allowCreate={false}
                      data-testid="parent-driver-combobox"
                    />
                  ) : (
                    <>
                      <StandardInput
                        {...register('name')}
                        data-testid="driver-name-input"
                      />
                      <ErrorMessage>{errors.name?.message}</ErrorMessage>
                    </>
                  )}
                </VStack>

                {/* Segment Name (for associating with regions) */}
                {isSegment && (
                  <VStack style={{ flexGrow: 0.5 }}>
                    <Label>
                      Segment name
                      {isSegment && <RequiredLabelStar>*</RequiredLabelStar>}
                    </Label>
                    <StandardComboBox
                      data={searchableRegions}
                      onChange={handleRegionChange}
                      placeholder={
                        parentDriverSelected
                          ? ''
                          : 'Select parent driver first'
                      }
                      maxSelected={1}
                      alwaysShowList={true}
                      valueList={selectedRegionsItems}
                      style={{
                        width: '12rem',
                        padding: '0px',
                        paddingTop: '0px',
                        paddingBottom: '0px',
                        alignItems: 'center',
                      }}
                      allowCreate={true}
                      onCreate={handleRegionCreate}
                      readOnly={!parentDriverSelected}
                      data-testid="segment-name-combobox"
                    />
                  </VStack>
                )}

                {/* Source and Description (hidden for segments) */}
                {!isSegment && (
                  <>
                    <VStack style={{ flexGrow: 0.5 }}>
                      <Label>
                        Source
                        <RequiredLabelStar>*</RequiredLabelStar>
                      </Label>
                      <StandardInput
                        {...register('source')}
                        data-testid="source-input"
                      />
                      <ErrorMessage>{errors.source?.message}</ErrorMessage>
                    </VStack>
                    <VStack style={{ flexGrow: 2 }}>
                      <Label>Description</Label>
                      <StandardInput
                        {...register('description')}
                        data-testid="description-input"
                      />
                      <ErrorMessage>{errors.description?.message}</ErrorMessage>
                    </VStack>
                  </>
                )}
              </DescriptionContainer>

              {/* DATASET CONFIGURATION (hidden for segments) */}
              {!isSegment && (
                <SelectorHStack>
                  {/* Category */}
                  <VStack style={{ flex: 0 }}>
                    <Label>
                      Category
                      <RequiredLabelStar>*</RequiredLabelStar>
                    </Label>
                    <FormSelectPicker
                      data={categoryOptions}
                      value={watch('category')}
                      onChange={(value) => {
                        setValue(
                          'category',
                          value as DatasetCreateRequestCategory,
                        )
                        if (value) clearErrors('category')
                      }}
                      style={{ width: widthSelectors }}
                      data-testid="category-select"
                    />
                    <ErrorMessage>{errors.category?.message}</ErrorMessage>
                  </VStack>

                  {/* Aggregation Method (only if frequency != monthly) */}
                  {watch('interval') && watch('interval') !== 'monthly' && (
                    <VStack style={{ flex: 0 }}>
                      <Label>Aggregation methods</Label>
                      <FormSelectPicker
                        data={aggregationMethodOptions}
                        value={watch('aggregation_method')}
                        onChange={(value) =>
                          setValue(
                            'aggregation_method',
                            value as DatasetCreateRequestAggregationMethod,
                          )
                        }
                        style={{ width: widthSelectors }}
                        data-testid="aggregation-method-select"
                      />
                      <ErrorMessage>
                        {errors.aggregation_method?.message}
                      </ErrorMessage>
                    </VStack>
                  )}

                  {/* Measure */}
                  <VStack style={{ flex: 0 }}>
                    <Label>
                      Measure
                      <RequiredLabelStar>*</RequiredLabelStar>
                    </Label>
                    <FormSelectPicker
                      data={unitOptions}
                      value={watch('units')}
                      onChange={(value) => {
                        setValue('units', value as DatasetCreateRequestUnits)
                        if (value) {
                          clearErrors('units')
                        }
                      }}
                      style={{ width: widthSelectors }}
                      data-testid="units-select"
                    />
                    <ErrorMessage>{errors.units?.message}</ErrorMessage>
                  </VStack>

                  {watch('part_of_media_channel') ? (
                    <>
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={
                          <Tooltip>
                            Cannot be combined with Media Channel
                          </Tooltip>
                        }
                      >
                        <VStack style={{ flex: 0 }}>
                          <Label>Influence</Label>
                          <FormSelectPicker
                            data={influenceOptions}
                            value={watch('influence')}
                            onChange={(value) =>
                              setValue(
                                'influence',
                                value as DatasetCreateRequestInfluence,
                              )
                            }
                            style={{ width: widthSelectors }}
                            data-testid="influence-select"
                            disabled={true}
                          />
                          <ErrorMessage>
                            {errors.influence?.message}
                          </ErrorMessage>
                        </VStack>
                      </Whisper>
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={
                          <Tooltip>
                            Cannot be combined with Media Channel
                          </Tooltip>
                        }
                      >
                        <VStack style={{ flex: 0 }}>
                          <Label>Investment</Label>
                          <FormSelectPicker
                            data={investmentOptions}
                            value={watch('investment')}
                            onChange={(value) =>
                              setValue(
                                'investment',
                                value as DatasetCreateRequestInvestment,
                              )
                            }
                            style={{ width: widthSelectors }}
                            data-testid="investment-select"
                            disabled={true}
                          />
                          <ErrorMessage>
                            {errors.investment?.message}
                          </ErrorMessage>
                        </VStack>
                      </Whisper>
                    </>
                  ) : (
                    <>
                      <VStack style={{ flex: 0 }}>
                        <Label>Influence</Label>
                        <FormSelectPicker
                          data={influenceOptions}
                          value={watch('influence')}
                          onChange={(value) =>
                            setValue(
                              'influence',
                              value as DatasetCreateRequestInfluence,
                            )
                          }
                          style={{ width: widthSelectors }}
                          data-testid="influence-select"
                        />
                        <ErrorMessage>{errors.influence?.message}</ErrorMessage>
                      </VStack>
                      <VStack style={{ flex: 0 }}>
                        <Label>Investment</Label>
                        <FormSelectPicker
                          data={investmentOptions}
                          value={watch('investment')}
                          onChange={(value) =>
                            setValue(
                              'investment',
                              value as DatasetCreateRequestInvestment,
                            )
                          }
                          style={{ width: widthSelectors }}
                          data-testid="investment-select"
                        />
                        <ErrorMessage>
                          {errors.investment?.message}
                        </ErrorMessage>
                      </VStack>
                    </>
                  )}
                </SelectorHStack>
              )}

              {/* MEDIA CHANNEL SECTION (only for media channel datasets) */}
              {!isSegment && watch('part_of_media_channel') && (
                <>
                  <Divider />
                  <InformationText>
                    Settings below apply only to media channel data for Optimize
                    MMM. If optional channel values are not set, they will be
                    set to defaults.
                  </InformationText>
                  <SelectorHStack>
                    {/* Channel */}
                    <VStack style={{ flex: 0 }}>
                      <Label>Channel</Label>
                      <StandardComboBox
                        data={searchableChannels}
                        onChange={handleChannelChange}
                        placeholder=""
                        maxSelected={1}
                        alwaysShowList={true}
                        valueList={selectedChannelsItems}
                        style={{ width: '12rem' }}
                        allowCreate={true}
                        onCreate={handleChannelCreate}
                        data-testid="channel-combobox"
                      />
                      <ErrorMessage>
                        {errors.media_channel_id?.message}
                      </ErrorMessage>
                    </VStack>

                    {/* Input Category (REQUIRED for media channel) */}
                    <VStack style={{ flex: 0 }}>
                      <Label>
                        Exposure category
                        <RequiredLabelStar>*</RequiredLabelStar>
                      </Label>
                      <FormSelectPicker
                        data={inputCategoryOptions}
                        value={watch('media_parameters.input_category')}
                        onChange={(value) => {
                          setValue(
                            'media_parameters.input_category',
                            value as MediaParametersRequestInputCategory,
                          )
                          if (value)
                            clearErrors('media_parameters.input_category')
                        }}
                        style={{ width: widthSelectors }}
                        data-testid="input-category-select"
                      />
                      <ErrorMessage>
                        {errors.media_parameters?.input_category?.message}
                      </ErrorMessage>
                    </VStack>

                    {/* Baseline (only for spend + high influence) */}
                    {watch('media_parameters.input_category') === 'spend' &&
                      watch('influence') === 'high' && (
                        <VStack style={{ flex: 0 }}>
                          <Label>Baseline for non-media</Label>
                          <FormSelectPicker
                            data={baselineForNonMediaOptions}
                            value={watch('non_media_baseline')}
                            onChange={(value) => {
                              setValue('non_media_baseline', value as any)
                            }}
                            style={{ width: widthSelectors }}
                            data-testid="baseline-select"
                          />
                          <ErrorMessage>
                            {errors.non_media_baseline?.message}
                          </ErrorMessage>
                        </VStack>
                      )}

                    {/* Channel Constraints (only for spend) */}
                    {watch('media_parameters.input_category') === 'spend' && (
                      <VStack style={{ flex: 0 }}>
                        <Label>Channel constraints (optional)</Label>
                        <StandardInput
                          type="number"
                          min={5}
                          max={100}
                          placeholder="+/-"
                          {...register('media_parameters.spend_constraint', {
                            valueAsNumber: true,
                          })}
                          style={{ width: widthSelectors }}
                          data-testid="spend-constraint-input"
                        />
                        <ErrorMessage>
                          {errors.media_parameters?.spend_constraint?.message}
                        </ErrorMessage>
                      </VStack>
                    )}

                    {/* Paired Reach (only for reach input category) */}
                    {watch('media_parameters.input_category') === 'reach' && (
                      <VStack style={{ flex: 1 }}>
                        <Label>Paired frequency dataset</Label>
                        <StandardComboBox
                          data={searchablePairableDatasets.map((item) => ({
                            id: item.id,
                            name: item.name,
                            disabled: !item.isActive,
                            tooltip: item.comment || undefined,
                          }))}
                          onChange={handlePairedFrequencyDatasetChange}
                          maxSelected={1}
                          alwaysShowList={true}
                          valueList={selectedPairedFrequencyDatasetItems}
                          allowCreate={false}
                          data-testid="paired-frequency-dataset-combobox"
                        />
                        <ErrorMessage>
                          {
                            errors.media_parameters?.paired_frequency_dataset_id
                              ?.message
                          }
                        </ErrorMessage>
                      </VStack>
                    )}

                    {/* Paired Frequency (only for frequency input category) */}
                    {watch('media_parameters.input_category') ===
                      'frequency' && (
                      <VStack style={{ flex: 1 }}>
                        <Label>Paired reach dataset</Label>
                        <StandardComboBox
                          data={searchablePairableDatasets.map((item) => ({
                            id: item.id,
                            name: item.name,
                            disabled: !item.isActive,
                            tooltip: item.comment || undefined,
                          }))}
                          onChange={handlePairedReachDatasetChange}
                          maxSelected={1}
                          alwaysShowList={true}
                          valueList={selectedPairedReachDatasetItems}
                          style={{
                            width: '100%',
                            padding: '0px',
                            paddingTop: '0px',
                            paddingBottom: '0px',
                            alignItems: 'center',
                          }}
                          allowCreate={false}
                          data-testid="paired-reach-dataset-combobox"
                        />
                        <ErrorMessage>
                          {
                            errors.media_parameters?.paired_reach_dataset_id
                              ?.message
                          }
                        </ErrorMessage>
                      </VStack>
                    )}

                    {/* ROI Assumption */}
                    {watch('media_parameters.input_category') === 'spend' && (
                      <VStack style={{ flex: 0 }}>
                        <Label>ROI assumption (optional)</Label>
                        <FormSelectPicker
                          data={roiAssumptionOptions}
                          value={watch('media_parameters.roi_assumption')}
                          onChange={(value) => {
                            setValue(
                              'media_parameters.roi_assumption',
                              value as MediaParametersRequestRoiAssumption,
                            )
                          }}
                          style={{ width: widthSelectors }}
                          data-testid="roi-assumption-select"
                        />
                        <ErrorMessage>
                          {errors.media_parameters?.roi_assumption?.message}
                        </ErrorMessage>
                      </VStack>
                    )}
                  </SelectorHStack>
                </>
              )}
            </VStackBroad>
            <ButtonContainer>
              <StandardButton type="submit">{submitButtonText}</StandardButton>
            </ButtonContainer>
          </FormGroup>
        </form>
      </FormProvider>
      {(serverError || showSuccess) && (
        <SuccessContainer>
          {serverError ? (
            <ServerErrorMessage>{serverError}</ServerErrorMessage>
          ) : (
            <CheckmarkAnimation show={showSuccess} text={successMessage} />
          )}
        </SuccessContainer>
      )}
      <FileValidationModal
        open={showInfoModal}
        handleClose={() => setShowInfoModal(false)}
        mode="information"
      />
      {/* Region Creation Modal */}
      <RegionCreateModal
        open={isRegionModalOpen}
        onClose={() => setIsRegionModalOpen(false)}
        onConfirm={handleRegionModalConfirm}
        initialName={pendingRegionName}
      />
    </Container>
  )
}

export default Form
